import React, { useId } from 'react';
import { useRotationsContext } from '../rotations-context';
import { Button, TextInput } from 'src/components/design-system';
import styles from './rotations-list-add-rotations.module.scss';
import { Modal, Switch, notification } from 'antd';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useCreateRotation, useCreateSchedule } from 'shared/hooks/rotations.hooks';
import { MultipleOwnersSelector } from 'src/components/owners/owners-selector/multiple-owners-selector';

const CREATE_ROTATION_SCHEMA = Yup.object().shape({
  name: Yup.string().required('Required!').min(3, 'Too Short!').max(100, 'Too Long!'),
  code: Yup.string().required('Required!').min(1, 'Too Short!'),
  ownerIds: Yup.array().of(Yup.string()),
  isAcgmeRequired: Yup.bool(),
});

const AddNewRotation = ({ CTA, createSchedule = false, buttonStyle = {} }) => {
  const { lang, setRotationId, setTab, tab, schedules } = useRotationsContext();
  const createRotation = useCreateRotation();
  const createScheduleMut = useCreateSchedule();
  const [addNewRotationModal, setAddNewRotationModal] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [notificationApi, contextHolder] = notification.useNotification({
    placement: 'bottomRight',
  });
  const nameInputId = useId();
  const codeInputId = useId();
  const directorsSelectId = useId();
  const acgmeSwitchId = useId();

  const createNewRotation = async values => {
    setLoading(true);

    let scheduleId = tab;

    try {
      if (createSchedule) {
        const scheduleRes = await createScheduleMut.mutateAsync({ name: 'All Rotations' });
        scheduleId = scheduleRes.id;
      }

      const data = await createRotation.mutateAsync({ ...values, scheduleId });

      notificationApi.success({
        title: lang.ROTATION_CREATED,
      });

      setRotationId(data.id);
      setTab(data.scheduleId);

      setAddNewRotationModal(false);
      resetForm();
    } catch (e) {
      notificationApi.error({
        title: lang.FAIL_TO_CREATE_ROTATION,
      });
    } finally {
      setLoading(false);
    }
  };

  const {
    handleSubmit,
    handleChange,
    handleBlur,
    setFieldValue,
    values,
    errors,
    isValid,
    resetForm,
  } = useFormik({
    initialValues: {
      name: '',
      code: '',
      ownerIds: [],
      isAcgmeRequired: false,
    },
    validationSchema: CREATE_ROTATION_SCHEMA,
    onSubmit: createNewRotation,
  });

  const setCode = e => {
    const { value, name } = e.target;
    setFieldValue(name, value.toUpperCase().trim());
  };

  const isScheduleTab = schedules.some(s => s.scheduleId === tab);

  if (!isScheduleTab && !createSchedule) {
    return null;
  }

  return (
    <div className={styles.addRotation}>
      <Button {...buttonStyle} onClick={() => setAddNewRotationModal(true)}>
        {CTA || lang.ADD_NEW_ROTATION}
      </Button>
      <Modal
        title={lang.ADD_NEW_ROTATION}
        open={addNewRotationModal}
        onOk={handleSubmit}
        okText={lang.ADD}
        cancelText={lang.CANCEL}
        okButtonProps={{
          loading,
          disabled: !isValid,
        }}
        cancelButtonProps={{
          disabled: loading,
        }}
        centered
        onCancel={() => setAddNewRotationModal(false)}
        afterOpenChange={isOpen => {
          if (!isOpen) resetForm();
        }}>
        <form onSubmit={handleSubmit}>
          <label htmlFor={nameInputId} className={styles.editNameLabel}>
            {lang.ROTATION_NAME}
          </label>
          <TextInput
            id={nameInputId}
            value={values.name}
            onChange={handleChange}
            onBlur={handleBlur}
            size="large"
            name="name"
            error={errors.name}
          />

          <label htmlFor={codeInputId} className={styles.editNameLabel}>
            {lang.ROTATION_CODE}
          </label>
          <TextInput
            id={codeInputId}
            value={values.code}
            onChange={setCode}
            onBlur={handleBlur}
            size="large"
            name="code"
            error={errors.code}
          />

          <label htmlFor={directorsSelectId}>{lang.ROTATION_DIRECTOR}</label>
          <div>
            <MultipleOwnersSelector
              id={directorsSelectId}
              size="large"
              name="ownerIds"
              errors={errors.ownerIds}
              onChange={(f, v) => setFieldValue(f, v)}
              value={values.ownerIds}
            />
          </div>

          <div className={styles.toggleRow}>
            <label htmlFor={acgmeSwitchId} className={styles.editNameLabel}>
              {lang.ACGME_REQUIRED}
            </label>
            <Switch
              id={acgmeSwitchId}
              checked={values.isAcgmeRequired}
              onChange={checked => setFieldValue('isAcgmeRequired', checked)}
            />
          </div>

          {schedules.length > 1 && (
            <>
              <label>{lang.SCHEDULE}</label>
              <p className={styles.scheduleName}>
                {schedules.find(s => s.scheduleId === tab).name}
              </p>
            </>
          )}
        </form>
      </Modal>
      {contextHolder}
    </div>
  );
};

export { AddNewRotation };
